/**
 * Horse racing (Konie) rules. No DOM, no Node — the web track and a later Expo
 * client have to land on the same finish and pay the same decimal odds.
 *
 * The live `/konie/` server is not in this repo. Client JS from production
 * (`js/constants.js`, `js/app.js`) is the source of truth:
 * six named horses, win/place/show + exacta/quinella/trifecta, decimal odds
 * (payout = round(stake × odds)), 12s race, chips 10/50/100/500/1000.
 *
 * Finish order is always chosen on the server. The client only animates a
 * published order; it never reports a winner.
 */

export const KN = {
  MIN_BET: 10,
  MAX_BET: 1_000_000,
  DEFAULT_BET: 50,
  CHIP_DENOMS: [10, 50, 100, 500, 1000] as const,
  HORSE_COUNT: 6,
  BETTING_MS: 18_000,
  RACE_MS: 12_000,
  RESULT_MS: 7_000,
  HOUSE_EDGE: 0.12,
  MIN_WIN_ODDS: 1.2,
  MIN_PLACE_ODDS: 1.15,
  MIN_SHOW_ODDS: 1.1,
  MIN_PAYOUT_EXACTA: 15,
  MIN_PAYOUT_QUINELLA: 8,
  MIN_PAYOUT_TRIFECTA: 40,
  HISTORY: 10,
  MAX_CHAT_LENGTH: 200,
  MAX_CHAT_HISTORY: 150,
  MAX_NAME_LENGTH: 30,
} as const;

export type KnSilk = "bolt" | "sash" | "star" | "stripe" | "dot" | "check";
export type KnCoat = "chestnut" | "black" | "grey" | "bay" | "palomino" | "brown";

export interface KnHorseDef {
  id: number;
  name: string;
  /** Jockey silk / saddle cloth. */
  color: string;
  accent: string;
  silk: KnSilk;
  coat: KnCoat;
}

/** Production field: Błyskawica, Cień, Diament, Grom, Iskra, Orzeł. */
export const HORSES: readonly KnHorseDef[] = [
  { id: 1, name: "Błyskawica", color: "#e53935", accent: "#ffd54f", silk: "bolt", coat: "chestnut" },
  { id: 2, name: "Cień", color: "#1e88e5", accent: "#e3f2fd", silk: "sash", coat: "black" },
  { id: 3, name: "Diament", color: "#f5c542", accent: "#212121", silk: "star", coat: "grey" },
  { id: 4, name: "Grom", color: "#43a047", accent: "#fff59d", silk: "stripe", coat: "bay" },
  { id: 5, name: "Iskra", color: "#8e24aa", accent: "#f3e5f5", silk: "dot", coat: "palomino" },
  { id: 6, name: "Orzeł", color: "#fb8c00", accent: "#fff3e0", silk: "check", coat: "brown" },
];

export const HORSE_NAMES: readonly string[] = HORSES.map((horse) => horse.name);

/** Long-run form. Favorites stay favorites; each race still swings. */
export const HORSE_FORM: readonly number[] = [1.35, 1.12, 1.05, 0.95, 0.88, 0.72];

export type KnBetKind = "win" | "place" | "show" | "exacta" | "quinella" | "trifecta";
export type KnPhase = "waiting" | "betting" | "racing" | "results";

export const BET_KIND_LABELS: Record<KnBetKind, string> = {
  win: "Wygrana",
  place: "Miejsce",
  show: "Pokaż",
  exacta: "Exacta",
  quinella: "Quinella",
  trifecta: "Trifecta",
};

export const BET_KIND_HELP: Record<KnBetKind, string> = {
  win: "Koń, który wygra wyścig (1. miejsce).",
  place: "Koń na 1. lub 2. miejscu.",
  show: "Koń w pierwszej trójce (1., 2. lub 3.).",
  exacta: "Dokładna kolejność: kto 1., kto 2. Kolejność ma znaczenie.",
  quinella: "Dwa konie na 1. i 2. — w dowolnej kolejności.",
  trifecta: "Dokładna kolejność 1., 2. i 3. miejsca.",
};

export const PHASE_LABELS: Record<KnPhase, string> = {
  waiting: "Oczekiwanie",
  betting: "Zakłady otwarte",
  racing: "Wyścig",
  results: "Wyniki",
};

export const SIMPLE_KINDS: readonly KnBetKind[] = ["win", "place", "show"];
export const EXOTIC_KINDS: readonly KnBetKind[] = ["exacta", "quinella", "trifecta"];

export interface KnOdds {
  win: number[];
  place: number[];
  show: number[];
  exacta: Record<string, number>;
  quinella: Record<string, number>;
  trifecta: Record<string, number>;
}

export interface KnPublicPlayer {
  userId: number;
  name: string;
  chips: number;
  keys: number;
  seated: true;
  color: string;
}

export interface KnPublicBet {
  id: string;
  userId: number;
  playerName: string;
  kind: KnBetKind;
  horses: number[];
  amount: number;
  odds: number;
}

export interface KnWinner {
  userId: number;
  playerName: string;
  kind: KnBetKind;
  horses: number[];
  amount: number;
  winnings: number;
  netProfit: number;
}

export interface KnRaceRecord {
  raceNumber: number;
  finishOrder: number[];
  winnerName: string;
}

export interface KnGameUpdate {
  phase: KnPhase;
  roundNumber: number;
  players: KnPublicPlayer[];
  bets: KnPublicBet[];
  odds: KnOdds;
  horseWins: number[];
  lastResults: KnRaceRecord[];
  finishOrder: number[] | null;
  bettingEndsAt: number | null;
  raceEndsAt: number | null;
  resultEndsAt: number | null;
  winners: KnWinner[];
}

export interface KnRaceCue {
  finishOrder: number[];
  startedAt: number;
  durationMs: number;
  roundNumber: number;
}

export interface KnChatMessage {
  user: string;
  text: string;
  ts: number;
  role: "player" | "dealer";
  isDealer?: boolean;
  cid?: string;
}

export function horseById(id: number): KnHorseDef | undefined {
  return HORSES.find((horse) => horse.id === id);
}

export function isHorseId(id: number): boolean {
  return Number.isInteger(id) && id >= 1 && id <= KN.HORSE_COUNT;
}

export function isKnBetKind(kind: string): kind is KnBetKind {
  return kind in BET_KIND_LABELS;
}

export function horsesNeeded(kind: KnBetKind): number {
  if (kind === "trifecta") return 3;
  if (kind === "exacta" || kind === "quinella") return 2;
  return 1;
}

export function clampKnBet(amount: number): number {
  if (!Number.isFinite(amount)) return KN.MIN_BET;
  return Math.min(KN.MAX_BET, Math.max(KN.MIN_BET, Math.floor(amount)));
}

export type KnParsedBet =
  | { ok: true; kind: KnBetKind; horses: number[]; amount: number }
  | { ok: false; reason: string };

function uniqueHorseList(raw: unknown, need: number): number[] | null {
  if (!Array.isArray(raw)) return null;
  const horses: number[] = [];
  for (const item of raw) {
    const id = typeof item === "number" ? item : Number(item);
    if (!isHorseId(id)) return null;
    horses.push(id);
  }
  if (horses.length !== need) return null;
  if (new Set(horses).size !== need) return null;
  return horses;
}

export function parseKnBet(payload: unknown): KnParsedBet {
  const body = (payload ?? {}) as {
    type?: unknown;
    kind?: unknown;
    horseId?: unknown;
    horses?: unknown;
    amount?: unknown;
  };
  const kindRaw = typeof body.kind === "string" ? body.kind : typeof body.type === "string" ? body.type : "";
  if (!isKnBetKind(kindRaw)) return { ok: false, reason: "Nieznany zakład." };
  const amount = clampKnBet(typeof body.amount === "number" ? body.amount : Number(body.amount));
  if (!Number.isFinite(Number(body.amount)) || Number(body.amount) < KN.MIN_BET) {
    return { ok: false, reason: "Wybierz zakład i kwotę (min. 10)." };
  }
  const need = horsesNeeded(kindRaw);
  let horses = uniqueHorseList(body.horses, need);
  if (!horses && need === 1) {
    const id = typeof body.horseId === "number" ? body.horseId : Number(body.horseId);
    if (isHorseId(id)) horses = [id];
  }
  if (!horses) {
    if (need === 1) return { ok: false, reason: "Wybierz konia." };
    if (need === 2) return { ok: false, reason: "Wybierz 2 różne konie." };
    return { ok: false, reason: "Wybierz 3 różne konie (1., 2., 3. miejsce)." };
  }
  return { ok: true, kind: kindRaw, horses, amount };
}

export function winProbabilities(weights: readonly number[]): number[] {
  const sum = weights.reduce((total, weight) => total + weight, 0);
  if (sum <= 0) return HORSES.map(() => 1 / HORSES.length);
  return weights.map((weight) => weight / sum);
}

export function exactaProb(p: readonly number[], firstIndex: number, secondIndex: number): number {
  if (firstIndex === secondIndex) return 0;
  const a = p[firstIndex] ?? 0;
  const b = p[secondIndex] ?? 0;
  const rest = 1 - a;
  if (rest <= 1e-12) return 0;
  return a * (b / rest);
}

export function trifectaProb(p: readonly number[], i: number, j: number, k: number): number {
  if (new Set([i, j, k]).size !== 3) return 0;
  const a = p[i] ?? 0;
  const b = p[j] ?? 0;
  const c = p[k] ?? 0;
  const r1 = 1 - a;
  if (r1 <= 1e-12) return 0;
  const r2 = r1 - b;
  if (r2 <= 1e-12) return 0;
  return a * (b / r1) * (c / r2);
}

export function placeProb(p: readonly number[], index: number): number {
  let total = p[index] ?? 0;
  for (let other = 0; other < p.length; other += 1) {
    if (other === index) continue;
    total += exactaProb(p, other, index);
  }
  return total;
}

export function showProb(p: readonly number[], index: number): number {
  let total = placeProb(p, index);
  for (let a = 0; a < p.length; a += 1) {
    if (a === index) continue;
    for (let b = 0; b < p.length; b += 1) {
      if (b === index || b === a) continue;
      total += trifectaProb(p, a, b, index);
    }
  }
  return total;
}

function decimalOdds(prob: number, floor: number): number {
  if (prob <= 1e-12) return floor;
  const raw = (1 / prob) * (1 - KN.HOUSE_EDGE);
  return Math.max(floor, Math.round(raw * 10) / 10);
}

export function computeOdds(weights: readonly number[]): KnOdds {
  const p = winProbabilities(weights);
  const win: number[] = [];
  const place: number[] = [];
  const show: number[] = [];
  for (let i = 0; i < HORSES.length; i += 1) {
    win.push(decimalOdds(p[i] ?? 0, KN.MIN_WIN_ODDS));
    place.push(decimalOdds(placeProb(p, i), KN.MIN_PLACE_ODDS));
    show.push(decimalOdds(showProb(p, i), KN.MIN_SHOW_ODDS));
  }
  const exacta: Record<string, number> = {};
  const quinella: Record<string, number> = {};
  const trifecta: Record<string, number> = {};
  for (let i = 0; i < HORSES.length; i += 1) {
    for (let j = 0; j < HORSES.length; j += 1) {
      if (i === j) continue;
      const hi = i + 1;
      const hj = j + 1;
      exacta[`${hi}-${hj}`] = decimalOdds(exactaProb(p, i, j), KN.MIN_PAYOUT_EXACTA);
      if (hi < hj) {
        quinella[`${hi}-${hj}`] = decimalOdds(
          exactaProb(p, i, j) + exactaProb(p, j, i),
          KN.MIN_PAYOUT_QUINELLA,
        );
      }
      for (let k = 0; k < HORSES.length; k += 1) {
        if (k === i || k === j) continue;
        trifecta[`${hi}-${hj}-${k + 1}`] = decimalOdds(trifectaProb(p, i, j, k), KN.MIN_PAYOUT_TRIFECTA);
      }
    }
  }
  return { win, place, show, exacta, quinella, trifecta };
}

export function emptyOdds(): KnOdds {
  return computeOdds(HORSE_FORM);
}

export function getBetOdds(odds: KnOdds, kind: KnBetKind, horses: readonly number[]): number | undefined {
  if (kind === "win" || kind === "place" || kind === "show") {
    const id = horses[0];
    if (id === undefined) return undefined;
    return odds[kind][id - 1];
  }
  if (kind === "exacta") {
    const a = horses[0];
    const b = horses[1];
    if (a === undefined || b === undefined) return undefined;
    return odds.exacta[`${a}-${b}`];
  }
  if (kind === "quinella") {
    const a = horses[0];
    const b = horses[1];
    if (a === undefined || b === undefined) return undefined;
    const lo = Math.min(a, b);
    const hi = Math.max(a, b);
    return odds.quinella[`${lo}-${hi}`];
  }
  const a = horses[0];
  const b = horses[1];
  const c = horses[2];
  if (a === undefined || b === undefined || c === undefined) return undefined;
  return odds.trifecta[`${a}-${b}-${c}`];
}

export function isKnBetWinner(
  kind: KnBetKind,
  horses: readonly number[],
  finishOrder: readonly number[],
): boolean {
  const first = finishOrder[0];
  const second = finishOrder[1];
  const third = finishOrder[2];
  if (first === undefined) return false;
  switch (kind) {
    case "win":
      return horses[0] === first;
    case "place":
      return horses[0] === first || horses[0] === second;
    case "show":
      return horses[0] === first || horses[0] === second || horses[0] === third;
    case "exacta":
      return horses[0] === first && horses[1] === second;
    case "quinella": {
      if (second === undefined) return false;
      const picked = new Set(horses.slice(0, 2));
      return picked.size === 2 && picked.has(first) && picked.has(second);
    }
    case "trifecta":
      return horses[0] === first && horses[1] === second && horses[2] === third;
  }
}

/** Total chips returned on a hit, stake already taken. Matches legacy `Math.round(amount * odds)`. */
export function koniePayout(amount: number, odds: number, won: boolean): number {
  if (!won) return 0;
  return Math.round(amount * odds);
}

export function rollRaceWeights(rng: () => number): number[] {
  return HORSE_FORM.map((base) => Math.max(0.18, base * (0.62 + rng() * 0.76)));
}

/**
 * Sequential weighted sample without replacement — same model as Harville
 * exacta/trifecta probabilities, so the board matches how the race is drawn.
 */
export function sampleFinishOrder(weights: readonly number[], rng: () => number): number[] {
  const remaining = HORSES.map((horse, index) => ({
    id: horse.id,
    weight: Math.max(0.01, weights[index] ?? HORSE_FORM[index] ?? 1),
  }));
  const order: number[] = [];
  while (remaining.length > 0) {
    const total = remaining.reduce((sum, row) => sum + row.weight, 0);
    let ticket = rng() * total;
    let pick = remaining.length - 1;
    for (let i = 0; i < remaining.length; i += 1) {
      const row = remaining[i];
      if (!row) continue;
      ticket -= row.weight;
      if (ticket <= 0) {
        pick = i;
        break;
      }
    }
    const chosen = remaining.splice(pick, 1)[0];
    if (chosen) order.push(chosen.id);
  }
  return order;
}

function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - (0 - 2 * t + 2) ** 3 / 2;
}

/**
 * Legacy client interpolation: horses jostle, the eventual winner pulls
 * ahead, everyone still crosses. `finalRank` is 0 for the winner.
 */
export function horseProgress(
  t: number,
  horseId: number,
  finalRank: number,
  finishOrder: readonly number[],
): number {
  const clamped = Math.max(0, Math.min(1, t));
  const tFast = Math.min(1, clamped * 1.12);
  const easeT = easeInOutCubic(tFast);
  const baseMax = 1.05 + (5 - finalRank) * 0.02;
  const omega = 2 * Math.PI * 3.5;
  const phase = finishOrder.indexOf(horseId) * 2.3;
  const integral = tFast + (0.8 * (Math.cos(phase) - Math.cos(omega * tFast + phase))) / omega;
  return easeT * baseMax + 0.12 * integral;
}

export function liveOrder(t: number, finishOrder: readonly number[]): number[] {
  return [...finishOrder]
    .map((horseId, finalRank) => ({
      horseId,
      progress: horseProgress(t, horseId, finalRank, finishOrder),
    }))
    .sort((a, b) => b.progress - a.progress)
    .map((row) => row.horseId);
}

export function formatKnBet(kind: KnBetKind, horses: readonly number[], names: readonly string[] = HORSE_NAMES): string {
  const label = BET_KIND_LABELS[kind];
  const bits = horses.map((id) => names[id - 1] ?? `#${id}`);
  if (kind === "win" || kind === "place" || kind === "show") {
    return `${label} ${bits[0] ?? ""}`.trim();
  }
  return `${label} ${bits.join("–")}`;
}

export function fmtChips(chips: number): string {
  if (!Number.isFinite(chips)) return "0";
  if (chips >= 1_000_000) return `${(chips / 1_000_000).toFixed(1)}M`;
  if (chips >= 1000) return `${(chips / 1000).toFixed(1)}K`;
  return chips.toLocaleString("pl-PL");
}

export function raceAchievementKeys(
  finishOrder: readonly number[],
  mine: readonly Pick<KnPublicBet, "kind" | "horses" | "amount">[],
): { keys: number; names: string[] } {
  const names: string[] = [];
  let keys = 0;
  const won = mine.filter((bet) => isKnBetWinner(bet.kind, bet.horses, finishOrder));
  if (won.some((bet) => bet.kind === "win")) {
    keys += 1;
    names.push("Fotofinisz");
  }
  if (won.some((bet) => bet.kind === "exacta")) {
    keys += 2;
    names.push("Exacta");
  }
  if (won.some((bet) => bet.kind === "trifecta")) {
    keys += 3;
    names.push("Trifecta");
  }
  if (won.some((bet) => bet.amount >= 1000)) {
    keys += 1;
    names.push("Duże ryzyko");
  }
  return { keys, names };
}
